import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Star, Trash2, Wand2, Loader2, Plus, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaywallModal } from "@/components/paywall-modal";
import { StudioProgress } from "@/components/studio-progress";
import { StudioOutputPanel } from "@/components/studio-output-panel";
import { FormatCommand } from "@/components/format-command";
import { FormatPickerModal } from "@/components/format-picker-modal";
import { StudioInputPanel } from "@/components/studio-input-panel";
import { StudioRemixModal } from "@/components/studio-remix-modal";
import { useStudio, STUDIO_DRAFT_KEY } from "@/hooks/use-studio";

export default function Studio() {
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const presetDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!presetDropdownOpen) return;
    function handleOutside(e: MouseEvent) {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target as Node)) {
        setPresetDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [presetDropdownOpen]);

  const {
    sessionId, setLocation,
    showPaywall, dismissPaywall,
    selectedPreset, setSelectedPreset,
    request, setRequest, handleRequestChange,
    output, setOutput,
    studioDrafts, setStudioDrafts,
    studioActiveDraftIdx, setStudioActiveDraftIdx,
    isGenerating, refining,
    copied, setCopied,
    copiedPlatform, platformCopyOpen, setPlatformCopyOpen,
    smartCopyLabel, shared, setShared,
    feedbackGiven, setFeedbackGiven,
    feedbackLoading, isSaving, saved, setSaved,
    generationId,
    studioUndoStack, setStudioUndoStack,
    voiceProfileDetected,
    resumeDraft, setResumeDraft,
    showStickyBar,
    studioAdvancedOpen, setStudioAdvancedOpen,
    advancedEverOpened, setAdvancedEverOpened,
    pinnedPresets, setPinnedPresets,
    showFormatPicker, setShowFormatPicker,
    studioTone, setStudioTone,
    studioPostStyle, setStudioPostStyle,
    studioHookStyle, setStudioHookStyle,
    studioSpacingStyle, setStudioSpacingStyle,
    studioPerspective, setStudioPerspective,
    studioHumorLevel, setStudioHumorLevel,
    studioAudience, setStudioAudience,
    studioEmojiUsage, setStudioEmojiUsage,
    studioTargetWordCount, setStudioTargetWordCount,
    studioCustomInstruction, setStudioCustomInstruction,
    studioVoiceState, setStudioVoiceState,
    studioAudienceDescription, setStudioAudienceDescription,
    studioBlendStyle, setStudioBlendStyle,
    studioRecentTopics, clearStudioContext,
    studioCoachTip, studioCoachLoading, studioCoachDismissed, setStudioCoachDismissed,
    ghostDraftLoading,
    copilotHint, setCopilotHint,
    inferredIntent, setInferredIntent,
    intentEditing, setIntentEditing,
    intentEditValue, setIntentEditValue,
    remixOpen, setRemixOpen,
    remixLoading,
    remixSourceText, setRemixSourceText,
    remixSourceFormat, setRemixSourceFormat,
    savedPresets, activePresetId,
    savePresetInputOpen, setSavePresetInputOpen,
    savePresetName, setSavePresetName,
    savingPreset,
    studioAdvancedCount, studioVoiceStates,
    clearStudioAdvanced,
    isRequestVague,
    studioVoiceInput,
    studioModeInfo,
    textareaRef, outputRef, generateBtnRef,
    handlePresetClick, handleLoadPreset, handleDeletePreset,
    handleSavePreset, handleGhostDraft,
    handleGenerate, handleRemix, handleRefine,
    handleStudioUndo, handleShare, handleResumeDraft,
    handleCopy, handleCopyForPlatform,
    handleRegenerate, handleFeedback, handleFeedbackSilent,
    handleSaveToVault,
    fetchStudioCoachTip,
    PINNED_KEY,
  } = useStudio();

  return (
    <div className="min-h-screen p-4 md:p-6 max-w-2xl mx-auto">
      <PaywallModal show={showPaywall} onDismiss={dismissPaywall} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mb-5"
      >
        <h1 className="text-xl font-display font-bold mb-0.5">Studio</h1>
        <p className="text-sm text-muted-foreground">
          Create anything in your voice — speeches, emails, pitches, meeting notes, and more.
        </p>
        {studioModeInfo.badgeLabel && (
          <button
            type="button"
            onClick={() => setLocation("/settings")}
            className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full bg-primary/8 border border-primary/15 text-[10px] font-medium text-primary/60 hover:text-primary hover:bg-primary/12 hover:border-primary/30 transition-all"
            title="Change your writing mode in Settings"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
            {studioModeInfo.badgeLabel}
          </button>
        )}
      </motion.div>

      {/* My Presets row */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.02 }}
        className="mb-3"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {/* Saved-preset dropdown — only when presets exist */}
          {savedPresets.length > 0 && (
            <div className="relative" ref={presetDropdownRef}>
              <button
                type="button"
                data-testid="saved-presets-trigger"
                onClick={() => setPresetDropdownOpen((o) => !o)}
                className={`inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-lg border text-xs font-medium transition-all duration-200 ${
                  activePresetId
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-border/70 hover:bg-secondary/50 hover:text-foreground"
                }`}
                style={activePresetId ? {
                  boxShadow: "0 0 0 1px hsl(188 90% 42% / 0.5), 0 0 20px hsl(188 90% 42% / 0.2)",
                } : undefined}
              >
                <Star className="w-3 h-3 shrink-0" />
                <span className="max-w-[140px] truncate">
                  {activePresetId
                    ? (savedPresets.find((p) => p.id === activePresetId)?.name ?? "My Presets")
                    : `My Presets (${savedPresets.length})`}
                </span>
                <ChevronDown
                  className={`w-3 h-3 shrink-0 transition-transform duration-200 ${presetDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {presetDropdownOpen && (
                  <motion.div
                    key="preset-dropdown"
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.97 }}
                    transition={{ duration: 0.13 }}
                    className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[280px] bg-card border border-border rounded-xl shadow-xl overflow-hidden"
                    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px hsl(var(--border))" }}
                  >
                    <div className="p-1 max-h-56 overflow-y-auto">
                      {savedPresets.map((preset) => (
                        <div
                          key={preset.id}
                          data-testid={`saved-preset-item-${preset.id}`}
                          role="button"
                          tabIndex={0}
                          className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                            activePresetId === preset.id
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => {
                            handleLoadPreset(preset);
                            setPresetDropdownOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleLoadPreset(preset);
                              setPresetDropdownOpen(false);
                            }
                          }}
                        >
                          <Star className="w-3 h-3 shrink-0 text-primary/60" />
                          <span className="text-xs font-medium flex-1 truncate">{preset.name}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.id, preset.name); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-muted-foreground hover:text-destructive transition-all shrink-0"
                            title="Remove preset"
                            data-testid={`delete-preset-${preset.id}`}
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Contextual Add-presets button — always visible */}
          <button
            type="button"
            data-testid="add-presets-btn"
            onClick={() => setShowFormatPicker(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-border/50 text-xs text-muted-foreground/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all duration-200"
          >
            <Plus className="w-3 h-3" />
            Add presets
          </button>
        </div>
      </motion.div>

      {/* Format Command Bar */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.04 }}
      >
        <FormatCommand
          selectedPreset={selectedPreset}
          pinnedPresets={pinnedPresets}
          onSelectPreset={(p) => {
            if (p) {
              handlePresetClick(p);
            } else {
              setSelectedPreset(null);
              setRequest("");
            }
          }}
          onOpenFormatPicker={() => setShowFormatPicker(true)}
        />
        <FormatPickerModal
          isOpen={showFormatPicker}
          onClose={() => setShowFormatPicker(false)}
          selected={pinnedPresets}
          onSelectionChange={(labels) => {
            setPinnedPresets(labels);
            try { localStorage.setItem(PINNED_KEY, JSON.stringify(labels)); } catch { /* ignore */ }
          }}
          suggestedCategoryLabels={studioModeInfo.suggestedCategories.length > 0 ? studioModeInfo.suggestedCategories : undefined}
        />
      </motion.div>

      {/* Resume draft chip */}
      <AnimatePresence>
        {resumeDraft && !output && !studioDrafts && !isGenerating && (
          <motion.div
            key="resume-draft-chip"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2 mb-3"
          >
            <button
              type="button"
              onClick={handleResumeDraft}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/6 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/12 hover:border-cyan-500/50 transition-all"
              style={{ boxShadow: "0 0 12px rgba(6,182,212,0.1)" }}
            >
              <Sparkles className="w-3 h-3 text-cyan-400" />
              Resume last draft
              {resumeDraft.format && (
                <span className="px-1.5 py-0.5 rounded-md bg-cyan-500/15 text-[10px] text-cyan-300/80 font-bold">
                  {resumeDraft.format}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setResumeDraft(null);
                try { localStorage.removeItem(STUDIO_DRAFT_KEY); } catch { /* ignore */ }
              }}
              className="text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
              aria-label="Dismiss draft"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input card */}
      <StudioInputPanel
        textareaRef={textareaRef as import("react").RefObject<HTMLTextAreaElement>}
        generateBtnRef={generateBtnRef as import("react").RefObject<HTMLButtonElement>}
        request={request}
        handleRequestChange={handleRequestChange}
        copilotHint={copilotHint}
        setCopilotHint={setCopilotHint}
        handleGenerate={handleGenerate}
        isGenerating={isGenerating}
        placeholder={studioModeInfo.placeholder}
        isRequestVague={isRequestVague}
        studioVoiceInput={studioVoiceInput}
        setRequest={setRequest}
        ghostDraftLoading={ghostDraftLoading}
        handleGhostDraft={handleGhostDraft}
        selectedPreset={selectedPreset}
        inferredIntent={inferredIntent}
        setInferredIntent={setInferredIntent}
        intentEditing={intentEditing}
        setIntentEditing={setIntentEditing}
        intentEditValue={intentEditValue}
        setIntentEditValue={setIntentEditValue}
        studioAdvancedOpen={studioAdvancedOpen}
        setStudioAdvancedOpen={setStudioAdvancedOpen}
        studioAdvancedCount={studioAdvancedCount}
        advancedEverOpened={advancedEverOpened}
        setAdvancedEverOpened={setAdvancedEverOpened}
        voiceStates={studioVoiceStates}
        studioVoiceState={studioVoiceState}
        setStudioVoiceState={setStudioVoiceState}
        studioAudienceDescription={studioAudienceDescription}
        setStudioAudienceDescription={setStudioAudienceDescription}
        studioTone={studioTone}
        setStudioTone={setStudioTone}
        studioPostStyle={studioPostStyle}
        setStudioPostStyle={setStudioPostStyle}
        studioHookStyle={studioHookStyle}
        setStudioHookStyle={setStudioHookStyle}
        studioSpacingStyle={studioSpacingStyle}
        setStudioSpacingStyle={setStudioSpacingStyle}
        studioPerspective={studioPerspective}
        setStudioPerspective={setStudioPerspective}
        studioHumorLevel={studioHumorLevel}
        setStudioHumorLevel={setStudioHumorLevel}
        studioAudience={studioAudience}
        setStudioAudience={setStudioAudience}
        studioEmojiUsage={studioEmojiUsage}
        setStudioEmojiUsage={setStudioEmojiUsage}
        studioTargetWordCount={studioTargetWordCount}
        setStudioTargetWordCount={setStudioTargetWordCount}
        studioCustomInstruction={studioCustomInstruction}
        setStudioCustomInstruction={setStudioCustomInstruction}
        studioBlendStyle={studioBlendStyle}
        setStudioBlendStyle={setStudioBlendStyle}
        studioRecentTopics={studioRecentTopics}
        clearStudioContext={clearStudioContext}
        clearStudioAdvanced={clearStudioAdvanced}
        savePresetInputOpen={savePresetInputOpen}
        setSavePresetInputOpen={setSavePresetInputOpen}
        savePresetName={savePresetName}
        setSavePresetName={setSavePresetName}
        savingPreset={savingPreset}
        handleSavePreset={handleSavePreset}
      />

      <AnimatePresence>
        {isGenerating && (
          <StudioProgress isActive={isGenerating} format={selectedPreset?.label ?? null} />
        )}
      </AnimatePresence>

      <StudioOutputPanel
        studioDrafts={studioDrafts}
        isGenerating={isGenerating}
        studioActiveDraftIdx={studioActiveDraftIdx}
        setStudioActiveDraftIdx={setStudioActiveDraftIdx}
        setStudioDrafts={setStudioDrafts}
        studioUndoStack={studioUndoStack}
        setStudioUndoStack={setStudioUndoStack}
        fetchStudioCoachTip={fetchStudioCoachTip}
        output={output ?? ""}
        setOutput={setOutput as import("react").Dispatch<import("react").SetStateAction<string>>}
        refining={refining}
        copied={copied}
        setCopied={setCopied}
        copiedPlatform={copiedPlatform}
        platformCopyOpen={platformCopyOpen}
        setPlatformCopyOpen={setPlatformCopyOpen}
        smartCopyLabel={smartCopyLabel}
        shared={shared}
        setShared={setShared}
        saved={saved}
        setSaved={setSaved}
        isSaving={isSaving}
        feedbackGiven={feedbackGiven}
        feedbackLoading={feedbackLoading}
        selectedPreset={selectedPreset}
        sessionId={sessionId}
        outputRef={outputRef as import("react").RefObject<HTMLDivElement>}
        studioCoachTip={studioCoachTip}
        studioCoachLoading={studioCoachLoading}
        studioCoachDismissed={studioCoachDismissed}
        setStudioCoachDismissed={setStudioCoachDismissed}
        voiceProfileDetected={voiceProfileDetected}
        generationId={generationId}
        setFeedbackGiven={setFeedbackGiven}
        setRemixOpen={setRemixOpen}
        setRemixSourceFormat={setRemixSourceFormat}
        setRemixSourceText={setRemixSourceText}
        handleCopy={handleCopy}
        handleCopyForPlatform={handleCopyForPlatform}
        handleShare={handleShare}
        handleRefine={handleRefine}
        handleRegenerate={handleRegenerate}
        handleSaveToVault={handleSaveToVault}
        handleStudioUndo={handleStudioUndo}
        handleFeedback={handleFeedback}
        handleFeedbackSilent={handleFeedbackSilent}
        onUndoPush={(prev) => {
          setOutput(prev);
          setStudioUndoStack(s => s.slice(1));
        }}
      />

      <StudioRemixModal
        remixOpen={remixOpen}
        setRemixOpen={setRemixOpen}
        remixLoading={remixLoading}
        handleRemix={handleRemix}
      />

      {/* Sticky Generate Bar */}
      <AnimatePresence>
        {showStickyBar && request.trim().length >= 5 && (
          <motion.div
            key="sticky-generate-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 36 }}
            className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pt-2"
            style={{ background: "linear-gradient(to top, hsl(var(--background)) 70%, transparent)" }}
          >
            <div
              className="max-w-lg mx-auto flex items-center gap-3 px-4 py-2.5 rounded-2xl border border-border/60 bg-card/95 backdrop-blur-md shadow-2xl"
              style={{ boxShadow: "0 0 0 1px hsl(188 90% 42% / 0.12), 0 8px 32px rgba(0,0,0,0.4)" }}
            >
              {selectedPreset ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  {(() => { const Icon = selectedPreset.icon; return <Icon className="w-3.5 h-3.5 text-cyan-400" />; })()}
                  <span className="text-xs font-semibold text-cyan-300 max-w-[140px] truncate">{selectedPreset.label}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 shrink-0">
                  <Wand2 className="w-3.5 h-3.5 text-muted-foreground/50" />
                  <span className="text-xs font-semibold text-muted-foreground/60">Studio</span>
                </div>
              )}
              <span className="text-[10px] text-muted-foreground/40 shrink-0">
                {request.trim().split(/\s+/).length}w
              </span>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                size="sm"
                className={`ml-auto h-8 px-4 rounded-lg font-semibold text-xs btn-shimmer${isGenerating ? " pulse-ring" : ""}`}
                style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))", color: "white" }}
              >
                {isGenerating ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Writing...</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate</>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
